/** The complete route table. Ids are unique; see routes.test.ts. */
import type { z } from 'zod';
import { hasAnyCapability, type Capability } from '../roles.js';
import { accountRoutes } from './accounts.js';
import { adminAccountRoutes } from './admin/accounts.js';
import { adminAiKeyRoutes } from './admin/aiKeys.js';
import { adminAuditRoutes } from './admin/audit.js';
import { adminOpsRoutes } from './admin/ops.js';
import { adminPeopleRoutes } from './admin/people.js';
import { adminTemplateRoutes } from './admin/templates.js';
import { adminUsageRoutes } from './admin/usage.js';
import { aiRoutes } from './ai.js';
import { rumiCodeRoutes } from './rumi-code.js';
import { auditRoutes } from './audit.js';
import { authRoutes } from './auth.js';
import type { RouteDef } from './common.js';
import { connectionRoutes } from './connections.js';
import { contentRoutes } from './content.js';
import { deviceRoutes } from './devices.js';
import { entitlementRoutes } from './entitlements.js';
// real-google change
import { integrationRoutes } from './integrations.js';
import { notificationRoutes } from './notifications.js';
import { providerLeaseRoutes } from './providerLeases.js';
import { usageRoutes } from './usage.js';

export const ROUTES = {
  ...authRoutes,
  ...deviceRoutes,
  ...entitlementRoutes,
  ...accountRoutes,
  ...connectionRoutes,
  ...contentRoutes,
  ...aiRoutes,
  ...rumiCodeRoutes,
  ...auditRoutes,
  ...usageRoutes,
  ...providerLeaseRoutes,
  ...notificationRoutes,
  // real-google change
  ...integrationRoutes,
  ...adminPeopleRoutes,
  ...adminAccountRoutes,
  ...adminAiKeyRoutes,
  ...adminTemplateRoutes,
  ...adminUsageRoutes,
  ...adminAuditRoutes,
  ...adminOpsRoutes,
} as const;

export type Routes = typeof ROUTES;
export type RouteId = keyof Routes;
export type RouteOf<Id extends RouteId> = Routes[Id];
export type RouteRequest<Id extends RouteId> = z.infer<Routes[Id]['requestSchema']>;
export type RouteResponse<Id extends RouteId> = z.infer<Routes[Id]['responseSchema']>;
export type RouteParams<Id extends RouteId> = Routes[Id] extends { paramsSchema: infer P extends z.ZodType } ? z.infer<P> : Record<string, never>;

export const ROUTE_IDS = Object.keys(ROUTES) as RouteId[];
export const ROUTE_LIST: readonly RouteDef[] = Object.values(ROUTES);

export function getRoute<Id extends RouteId>(id: Id): RouteOf<Id> {
  return ROUTES[id];
}

/** True when `caps` satisfy the route's capability requirement (any-of). */
export function routeAllows(route: RouteDef, caps: Iterable<Capability>): boolean {
  if (!route.capability) return true;
  return hasAnyCapability(caps, route.capability);
}

/** Replace `:param` segments with values from `params`. */
export function buildPath(path: string, params: Record<string, string> = {}): string {
  return path.replace(/:([A-Za-z][A-Za-z0-9_]*)/g, (_m, name: string) => {
    const value = params[name];
    if (value === undefined) throw new Error(`missing path param ${name}`);
    return encodeURIComponent(value);
  });
}

export function routesByAuth(auth: RouteDef['auth']): RouteDef[] {
  return ROUTE_LIST.filter((r) => r.auth === auth);
}
